import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Hammer } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(location.state?.from?.pathname || "/feed", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Craftsman"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/20" />
        <div className="relative h-full flex flex-col justify-end p-10 text-white">
          <div className="font-heading text-3xl font-extrabold leading-tight max-w-md">
            Real people, real work, right in your neighborhood.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl bg-[#3E2A4E] text-[#D9FF3D] grid place-items-center">
              <Hammer size={18} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">Day Hire</span>
          </Link>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="text-sm text-slate-600 mt-1">Log in to continue.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="mt-1.5 h-11" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full h-11 bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full btn-primary-glow">
              {loading ? "Signing in…" : "Log in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />
            <span>OR</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <GoogleButton testid="login-google-btn" />

          <p className="mt-6 text-sm text-slate-600">
            New here?{" "}
            <Link to="/register" className="font-semibold text-[#3E2A4E] hover:underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
